/**
 * ボム能力関連の処理（BOMBER専用機）
 *
 * このモジュールはボムの使用、持続ダメージ（ティック処理）、
 * 画面上の爆発演出データの生成を管理します。
 *
 * @module abilities/bomb
 */

import { CONFIG } from '../../config/game-config';
import {
    getBombStock,
    setBombStock,
    getIsBombActive,
    setIsBombActive,
    setBombEffectTimer,
    setBombDamageTimer,
    setBombTickTimer,
    getBombDamageTimer,
    getBombTickTimer,
    setScreenShake,
    setBombExplosions,
    getPowerLevel,
    getEnemies,
    setEnemies,
    setEnemyBullets
} from '../../store/index';
import { handleEnemyDefeat } from '../enemy/enemy';
import { createExplosion } from '../particles/particles';
import type { BombExplosion } from '../../types/game-types';
import { getP5 } from '../../core/p5-instance';
import { updateBombEffect } from './bomb-effects';

/**
 * ボムの使用処理
 * ストックを消費し、画面全体の敵にダメージを与えるボムを発動します。
 */
export function useBomb(): void {
    const p = getP5();
    const bombStock = getBombStock();
    const isBombActive = getIsBombActive();
    const powerLevel = getPowerLevel();

    // ストック不足、または既に発動中の場合は何もしない
    if (bombStock <= 0 || isBombActive) {
        return;
    }

    setBombStock(bombStock - 1);
    setIsBombActive(true);

    // パワーレベルに応じたボム設定を取得（Lv1〜Lv5）
    const bombCfg = CONFIG.ships.bomber.bombByPower;
    const lvIndex = p.constrain(powerLevel - 1, 0, 4);
    const effectScale = bombCfg.effectScale[lvIndex];

    setBombEffectTimer(90); // 視覚エフェクトの持続時間（約1.5秒）
    // ダメージ持続時間はパワーレベルに応じて変化
    setBombDamageTimer(bombCfg.damageDuration[lvIndex]);
    // ダメージティックの即時実行用
    setBombTickTimer(0);
    setScreenShake(20); // 発動時の大きな揺れ

    // 視覚的な爆発エフェクトの初期位置データを生成
    const bombExplosions: BombExplosion[] = [];

    // 画面全体にランダムな爆発を配置（パワーレベルで数が増加）
    const explosionCount = 10 + powerLevel * 2;  // Lv1:12個 〜 Lv5:20個
    for (let i = 0; i < explosionCount; i++) {
        bombExplosions.push({
            x: p.random(30, p.width - 30),
            y: p.random(50, p.height - 100),
            delay: i * 3, // 少しずつずらして発生させる
            size: 0,
            maxSize: p.random(100, 180) * effectScale,
            timer: 0,
            color: p.random() > 0.3 ? p.color(255, 180, 50) : p.color(255, 100, 30)
        });
    }
    setBombExplosions(bombExplosions);

    // 発動直後のダメージ判定（派手なパーティクル演出あり + 敵弾消去）
    performBombTick(true);
    // 次のティックまでの間隔を設定
    setBombTickTimer(bombCfg.damageInterval[lvIndex]);
}

/**
 * ボムの状態更新ロジック
 * 持続時間とティック間隔のカウントダウンを行い、定期的にダメージを発生させます。
 *
 * @param dt 経過時間（フレーム倍率）
 */
export function updateBomb(dt: number): void {
    if (!getIsBombActive()) {
        return;
    }

    // 視覚演出（爆発アニメーション）の状態更新
    updateBombEffect();

    const p = getP5();
    let damageTimer = getBombDamageTimer();
    let bombTickTimer = getBombTickTimer();

    // 持続ダメージ期間中の処理
    if (damageTimer > 0) {
        damageTimer -= dt;
        bombTickTimer -= dt;
        setBombDamageTimer(damageTimer);

        // 一定間隔（ティック）ごとにダメージ判定を実行
        if (bombTickTimer <= 0) {
            const powerLevel = getPowerLevel();
            const bombCfg = CONFIG.ships.bomber.bombByPower;
            const lvIndex = p.constrain(powerLevel - 1, 0, 4);

            performBombTick(false); // 継続ティックでは重い演出は控える
            setBombTickTimer(bombCfg.damageInterval[lvIndex]);
        } else {
            setBombTickTimer(bombTickTimer);
        }
    }
}

/**
 * ボムのダメージティック処理
 * 画面上の全敵へのダメージ適用と敵弾の全消去を行います。
 *
 * @param withFX 撃破エフェクトとは別の追加演出（発動時のみ）を行うかどうか
 */
function performBombTick(withFX: boolean): void {
    const p = getP5();
    const powerLevel = getPowerLevel();
    const enemies = getEnemies();

    // 画面上の敵弾をすべて消去
    setEnemyBullets([]);

    // パワーレベルに応じたダメージ量を取得
    const bombCfg = CONFIG.ships.bomber.bombByPower;
    const lvIndex = p.constrain(powerLevel - 1, 0, 4);
    const damage = bombCfg.damage[lvIndex];

    // 全ての敵に対してループ
    for (let i = enemies.length - 1; i >= 0; i--) {
        const enemy = enemies[i];
        enemy.hp -= damage;

        // 指定された場合は追加の爆発演出を生成
        if (withFX) {
            createExplosion(enemy.x, enemy.y, enemy.color);
        }

        // 敵の撃破判定
        if (enemy.hp <= 0) {
            // 撃破処理（ボムによる撃破はアイテムドロップ率を低下させてバランス調整）
            handleEnemyDefeat(enemy, {
                lowDropRate: true,
                skipExplosion: withFX  // withFXですでに演出を出している場合は重複を避ける
            });
            enemies.splice(i, 1);
        }
    }

    // 変化した敵リストをステートに反映
    setEnemies(enemies);
}